import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Landmark, Users, Heart, BookOpen, Globe, MessageCircle, Star, CheckCircle, Sparkles, Award, Camera, GraduationCap, Handshake, Crown, Building2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function PoliticiansPage() {
  return <LanguageProvider data-id="element-6154">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-6155">
        <ScrollProgress data-id="element-6156" />
        <Navbar data-id="element-6157" />
        <main className="w-full" data-id="element-6158">
          <ConstituencyHero data-id="element-6159" />
          <WhyItMattersSection data-id="element-6160" />
          <TheExperienceSection data-id="element-6161" />
          <InvolvementOptionsSection data-id="element-6162" />
          <ConstituencyNightSection data-id="element-6163" />
          <ConstituencyCTA data-id="element-6164" />
        </main>
        <Footer data-id="element-6165" />
      </div>
    </LanguageProvider>;
}
function ConstituencyHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-6166">
      <div className="absolute inset-0" data-id="element-6167">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-25" data-id="element-6168" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-6169"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-6170">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-6171">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-6172">
            <ArrowLeft className="w-4 h-4" data-id="element-6173" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-6174">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-6175">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-6176">
            Constituency &amp; Cultural Night
          </p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white mb-8 tracking-tight leading-none" data-id="element-6177">
            COMMUNITY &amp;
            <br data-id="element-6178" />
            <span className="text-amber-400" data-id="element-6179">CONSTITUENCY NIGHT</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-6180"></div>
          <p className="text-lg md:text-xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-6181">
            A Nonpartisan Cultural Celebration of Heritage, Education &amp;
            Multicultural Excellence — A Community Economic Win for Your Riding
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-6182">
          <div className="text-center" data-id="element-6183">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-6184">
              Heritage
            </p>
            <p className="font-serif text-xl text-white" data-id="element-6185">Education</p>
            <p className="text-xs font-light" data-id="element-6186">Cultural Preservation</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-6187"></div>
          <div className="text-center" data-id="element-6188">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-6189">
              Arts
            </p>
            <p className="font-serif text-xl text-white" data-id="element-6190">Innovation</p>
            <p className="text-xs font-light" data-id="element-6191">Multicultural Excellence</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-6192"></div>
          <div className="text-center" data-id="element-6193">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-6194">
              Tourism
            </p>
            <p className="font-serif text-xl text-white" data-id="element-6195">City Pride</p>
            <p className="text-xs font-light" data-id="element-6196">Community Economic Win</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-6197">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-6198"></div>
      </motion.div>
    </section>;
}
function WhyItMattersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6199">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-6200"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-6201">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-6202">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-6203">
            The Civic Case
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-6204">
            WHY IT <span className="text-amber-400" data-id="element-6205">MATTERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-6206"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-6207">
            For MPs, MPPs, councillors, and community leaders, this is a rare
            nonpartisan opportunity to champion heritage, education, and
            multicultural excellence in your riding.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-6208">
          {[{
          icon: <BookOpen className="w-8 h-8 text-amber-400 mb-4" data-id="element-6209" />,
          title: 'Heritage & Education',
          body: 'A living classroom connecting youth, seniors, and newcomers to 200+ years of Sikh history — through taste, sight, sound, and storytelling. Preserving culture through innovation.'
        }, {
          icon: <Globe className="w-8 h-8 text-amber-400 mb-4" data-id="element-6210" />,
          title: 'Multicultural Excellence',
          body: "Showcasing South Asian culture as a cornerstone of Canada's multicultural identity. An event that brings diaspora leaders, interfaith communities, and newcomers together under one roof."
        }, {
          icon: <Award className="w-8 h-8 text-amber-400 mb-4" data-id="element-6211" />,
          title: 'Arts Innovation',
          body: 'Oscar-winning animation, immersive dining, and interactive technology — pioneering a new form of cultural experience that positions your riding at the forefront of arts and culture in Canada.'
        }, {
          icon: <Landmark className="w-8 h-8 text-amber-400 mb-4" data-id="element-6212" />,
          title: 'Tourism & City Pride',
          body: 'Positioning Toronto as a global destination for innovative cultural experiences. Generates local economic activity, media coverage, and community pride — a tangible win for your riding.'
        }].map((card, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-6213">
              {card.icon}
              <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-6214">
                {card.title}
              </h3>
              <p className="text-white/70 font-light leading-relaxed" data-id="element-6215">
                {card.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </motion.section>;
}
function TheExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6216">
      <div className="container mx-auto px-4" data-id="element-6217">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-6218">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-6219">
            What Your Guests Will Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-6220">
            THE <span className="text-amber-400" data-id="element-6221">EXPERIENCE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-6222"></div>
        </motion.div>

        <div className="max-w-6xl mx-auto" data-id="element-6223">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-6224">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-6225">
              <div className="aspect-video relative overflow-hidden" data-id="element-6226">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Immersive Experience" className="w-full h-full object-cover" data-id="element-6227" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-6228"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-6229">
              <p className="text-white/80 text-lg leading-relaxed font-light" data-id="element-6230">
                Life in the Sikh Kingdoms is an immersive dining and cultural
                experience that brings Maharaja Ranjit Singh's era to life
                through taste, sight, sound, and storytelling — at Toronto's
                Illuminarium.
              </p>
              <p className="text-white/70 leading-relaxed font-light" data-id="element-6231">
                This is not theater. This is not dining. This is a portal to
                1825 Lahore — a nonpartisan cultural institution celebrating
                heritage, education, multicultural excellence, arts innovation,
                and city pride.
              </p>

              <div className="border-l-2 border-amber-400/30 pl-6 space-y-4" data-id="element-6232">
                {['5–7 courses synchronized with immersive projections', '360° visual storytelling across walls, floor, and tables', 'Royal Punjabi cuisine from world-renowned chefs', '90 minutes of complete cultural immersion', 'Interactive technology — voice, motion, and scent'].map((item, i) => <div key={i} className="flex items-start" data-id="element-6233">
                    <span className="text-amber-400 mr-3 mt-1" data-id="element-6234">—</span>
                    <span className="text-white/70 font-light" data-id="element-6235">{item}</span>
                  </div>)}
              </div>

              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-6236">
                <div className="grid grid-cols-3 gap-4 text-center" data-id="element-6237">
                  <div data-id="element-6238">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-6239">
                      Venue
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-6240">
                      Illuminarium Toronto
                    </p>
                  </div>
                  <div data-id="element-6241">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-6242">
                      Date
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-6243">Summer 2026</p>
                  </div>
                  <div data-id="element-6244">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-6245">
                      Capacity
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-6246">
                      Up to 120 Guests
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-amber-400/40 p-6 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-6247">
                <p className="text-white/70 font-light text-sm leading-relaxed" data-id="element-6248">
                  <span className="text-amber-400 font-medium" data-id="element-6249">
                    ROM Legacy:{' '}
                  </span>
                  This experience honors the groundbreaking "Art of the Sikh
                  Kingdoms" exhibition at the Royal Ontario Museum —{' '}
                  <strong className="text-white" data-id="element-6250">
                    the second most popular exhibition in ROM history
                  </strong>
                  .
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function InvolvementOptionsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6251">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-6252"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-6253">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-6254">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-6255">
            Simple Menu of Options
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-6256">
            WAYS TO <span className="text-amber-400" data-id="element-6257">GET INVOLVED</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-6258"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-6259">
            Whether you're an MP, MPP, city councillor, or community leader —
            choose the level of involvement that fits your schedule and
            priorities.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-6260">
          {/* Option A */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="relative" data-id="element-6261">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-6262">
              <div className="bg-amber-400 text-black font-bold py-1.5 px-5 flex items-center gap-2" data-id="element-6263">
                <Star className="w-3 h-3" fill="currentColor" data-id="element-6264" />
                <span className="text-xs uppercase tracking-wider" data-id="element-6265">
                  Recommended
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-10 h-full" data-id="element-6266">
              <div className="flex items-center gap-3 mb-4" data-id="element-6267">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-6268">A</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-6269">
                  Patron of the Night
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-6270">
                Constituency Night Host
              </p>
              <ul className="space-y-3" data-id="element-6271">
                {['Host the Constituency Night as Guest of Honour', 'Short welcome remarks (5–7 minutes)', 'Photo moment + community recognition segment', 'Press photo call with community leaders'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-6272">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-6273" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Option B */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-6274">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full" data-id="element-6275">
              <div className="flex items-center gap-3 mb-4" data-id="element-6276">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-6277">B</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-6278">
                  Cultural Roundtable
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-6279">
                Community Leader Convening
              </p>
              <ul className="space-y-3" data-id="element-6280">
                {['Private preview for community leaders & sponsors', 'Convene diaspora, cultural orgs, and business leaders', 'Sponsorship ladder pitch opportunity'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-6281">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-6282" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Option C */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-6283">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full" data-id="element-6284">
              <div className="flex items-center gap-3 mb-4" data-id="element-6285">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-6286">C</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-6287">
                  Youth Heritage Night
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-6288">
                Education &amp; Legacy
              </p>
              <ul className="space-y-3" data-id="element-6289">
                {['Student tickets + mentorship tie-in', 'Educator guide + talkback session', 'Highlight youth & heritage mandate'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-6290">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-6291" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Option D */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-6292">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full" data-id="element-6293">
              <div className="flex items-center gap-3 mb-4" data-id="element-6294">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-6295">D</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-6296">
                  Sponsor Drive
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-6297">
                Business Community Activation
              </p>
              <ul className="space-y-3" data-id="element-6298">
                {['Introduce 5–10 local sponsor prospects', 'Warm intros to local businesses in your riding', 'We close the deals — minimal time required'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-6299">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-6300" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function ConstituencyNightSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6301">
      <div className="container mx-auto px-4" data-id="element-6302">
        <div className="max-w-5xl mx-auto" data-id="element-6303">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-6304">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-6305">
              The Vision
            </p>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-6306">
              CONSTITUENCY NIGHT{' '}
              <span className="text-amber-400" data-id="element-6307">DONE RIGHT</span>
            </h2>
            <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-6308"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-6309">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-6310">
              <div className="border-2 border-amber-400/40 p-8 md:p-10 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-6311">
                <h3 className="font-serif text-2xl text-amber-400 mb-6" data-id="element-6312">
                  What It Looks Like
                </h3>
                <div className="space-y-5" data-id="element-6313">
                  {[{
                  icon: <Crown className="w-4 h-4 text-amber-400" data-id="element-6314" />,
                  title: 'Guest of Honour Welcome',
                  sub: '5–7 minute opening remarks from the elected official or community leader'
                }, {
                  icon: <Star className="w-4 h-4 text-amber-400" data-id="element-6315" />,
                  title: 'Recognition of Community Builders',
                  sub: 'Acknowledge local leaders, volunteers, and cultural champions'
                }, {
                  icon: <Heart className="w-4 h-4 text-amber-400" data-id="element-6316" />,
                  title: 'Optional Nonprofit Tie-In',
                  sub: 'Keeps it civic, not political — fundraising for ROM Sikh gallery'
                }, {
                  icon: <Users className="w-4 h-4 text-amber-400" data-id="element-6317" />,
                  title: 'Group Tickets for Local Orgs',
                  sub: 'Youth seats sponsored by a local business — full room guaranteed'
                }, {
                  icon: <Camera className="w-4 h-4 text-amber-400" data-id="element-6318" />,
                  title: 'Press Photo Call',
                  sub: 'Professional photography, ethnic media coverage, community papers'
                }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-6319">
                      <div className="w-8 h-8 flex items-center justify-center border border-amber-400/30 flex-shrink-0 mt-0.5" data-id="element-6320">
                        {item.icon}
                      </div>
                      <div data-id="element-6321">
                        <p className="text-white font-light text-sm mb-1" data-id="element-6322">
                          {item.title}
                        </p>
                        <p className="text-white/50 text-xs font-light" data-id="element-6323">
                          {item.sub}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-6324">
              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-6325">
                <h4 className="font-serif text-xl text-amber-400 mb-4" data-id="element-6326">
                  The Result
                </h4>
                <div className="space-y-3" data-id="element-6327">
                  {['Full room with engaged community members', 'Strong press photos for all parties', 'Goodwill across diaspora, cultural, and civic communities', 'Sponsor confidence from dignitary presence', 'Media coverage in ethnic and community press'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-6328">
                      <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" data-id="element-6329" />
                      <span className="text-white/70 text-sm font-light" data-id="element-6330">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="border-2 border-amber-400/40 p-6 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-6331">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-3 font-light" data-id="element-6332">
                  Who Gets Invited
                </p>
                <div className="space-y-2 text-white/60 text-sm font-light" data-id="element-6333">
                  <p data-id="element-6334">— Diaspora leaders &amp; cultural organizations</p>
                  <p data-id="element-6335">— School boards &amp; newcomer services</p>
                  <p data-id="element-6336">— Business improvement areas (BIAs)</p>
                  <p data-id="element-6337">— Tourism groups &amp; arts councils</p>
                  <p data-id="element-6338">— Local sponsors (banks, telcos, hotels)</p>
                  <p data-id="element-6339">— Consular reps &amp; interfaith leaders</p>
                </div>
              </div>

              <div className="border-2 border-amber-400/40 p-6 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-6340">
                <Heart className="w-6 h-6 text-amber-400 mb-3" data-id="element-6341" />
                <p className="text-amber-400 italic font-light text-sm leading-relaxed" data-id="element-6342">
                  "A portion of all proceeds goes directly to the Sikh Art and
                  Culture Gallery at the Royal Ontario Museum, preserving Sikh
                  heritage for future generations."
                </p>
                <p className="text-white/40 text-xs font-light mt-3 tracking-wider uppercase" data-id="element-6343">
                  Every ticket funds cultural storytelling + community uplift
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function ConstituencyCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6344">
      <div className="container mx-auto px-4" data-id="element-6345">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-6346">
          <Landmark className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-6347" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-6348">
            Let's Build Something{' '}
            <span className="text-amber-400" data-id="element-6349">Meaningful Together</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-6350"></div>
          <p className="text-xl text-white/70 font-light mb-4 leading-relaxed" data-id="element-6351">
            We welcome the opportunity to discuss how this cultural experience
            can serve your constituency and community.
          </p>
          <p className="text-white/50 font-light mb-10 leading-relaxed" data-id="element-6352">
            This is a nonpartisan cultural institution open to MPs, MPPs, city
            councillors, and community leaders of all backgrounds. Our goal is
            to celebrate heritage, support education, and create a community
            economic win for your riding — together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-6353">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20discussing%20a%20constituency%20night%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-6354">
              <MessageCircle className="w-5 h-5" data-id="element-6355" />
              Start the Conversation
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-6356">
              <ArrowLeft className="w-4 h-4" data-id="element-6357" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-6358">
            Our team will respond within 24 hours to discuss partnership
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}